import React from "react";
import { Box } from "@mui/material";
import { Link } from "react-router-dom";
import logoFile from "../../Images/Appbar/Logo.png";
import { color, font, motion } from "../../design/tokens";

/**
 * The Five Lights Reno lockup.
 *
 * The supplied logo file is a stacked mark: the gold arch sits above a
 * wordmark. Stacked marks don't work in a 70–90px header — at any height that
 * fits, the wordmark is about seven pixels tall and unreadable.
 *
 * So the header uses a horizontal lockup instead: the client's arch mark
 * (cropped from the top 60% of the same file, which clears the wordmark with
 * room either side) set beside the company name typeset in Manrope. The mark
 * itself is untouched — this only changes how the two parts are arranged.
 *
 * `stacked` renders the original file whole, for the footer where there's
 * vertical room for it to be read as supplied.
 */
const Logo = ({ tone = "ink", stacked = false, markHeight = 30, sx }) => {
  const wordmarkColor = tone === "onInk" ? color.onInk : color.ink;

  if (stacked) {
    return (
      <Box
        component={Link}
        to="/"
        aria-label="Five Lights Reno — home"
        sx={{ display: "inline-block", ...sx }}
      >
        <Box
          component="img"
          src={logoFile}
          alt=""
          sx={{ width: 168, height: "auto" }}
        />
      </Box>
    );
  }

  return (
    <Box
      component={Link}
      to="/"
      aria-label="Five Lights Reno — home"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: { xs: 1.25, md: 1.75 },
        transition: `opacity ${motion.base} ${motion.ease}`,
        "&:hover": { opacity: 0.72 },
        ...sx,
      }}
    >
      {/* Arch mark, cropped out of the stacked source file.
          In the 566x424 original the arch occupies roughly y 90–245 and the
          wordmark starts near y 270. A 3.65:1 window (566 wide x ~155 tall)
          therefore frames the arch and clears the wordmark entirely, and
          `object-position: center 33%` lands that window on it — worked back
          from `cover`'s scale so the arch fills the box instead of floating in
          the empty space above it. */}
      <Box
        sx={{
          width: { xs: 22 * 3.65, md: markHeight * 3.65 },
          height: { xs: 22, md: markHeight },
          overflow: "hidden",
          flexShrink: 0,
          transition: `width ${motion.base} ${motion.ease}, height ${motion.base} ${motion.ease}`,
        }}
      >
        <Box
          component="img"
          src={logoFile}
          alt=""
          aria-hidden="true"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center 33%",
          }}
        />
      </Box>

      <Box
        component="span"
        sx={{
          fontFamily: font.body,
          fontWeight: 800,
          fontSize: { xs: "0.8125rem", md: "0.875rem" },
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          lineHeight: 1,
          color: wordmarkColor,
          whiteSpace: "nowrap",
          transition: `color ${motion.base} ${motion.ease}`,
        }}
      >
        Five Lights Reno
      </Box>
    </Box>
  );
};

export default Logo;
